/**
 * `sync` procedure group — connected provider accounts and their health.
 *
 * This is the contract behind `/settings/accounts` (clients design §2.9) and the
 * `sync.status` watermark dashboard the plan lists under Ops. Two things it
 * deliberately does NOT expose:
 *
 *  - **no token material, ever.** Not the ciphertext, not the wrapped DEK, not
 *    the OAuth client secret. A settings screen has no use for them and a schema
 *    that carried them would invite a handler to serialise them.
 *  - **no live provider call.** Calendars come from the local
 *    `connected_calendars` mirror, so the screen renders when a token needs
 *    refreshing and stays fast.
 */

import { z } from 'zod';
import { isoDateTimeSchema, uuidSchema } from './common.ts';
import {
  backfillStateSchema,
  connectedAccountStatusSchema,
  syncHealthSchema,
  syncProviderSchema,
} from './enums.ts';

export const connectedAccountSchema = z.object({
  id: uuidSchema,
  provider: syncProviderSchema,
  /** Google `sub` / Graph object id. Stable across reconnects. */
  providerAccountId: z.string(),
  email: z.string(),
  displayName: z.string().nullable(),
  /**
   * WHICH OAuth client authorised this account — `google_internal`,
   * `google_external`, `ms_multitenant`, `unipile`. Per ACCOUNT, not per tenant
   * (research: "you will end up with multiple Google clients"), and surfaced so
   * an operator can tell a Workspace-internal grant from an external one.
   */
  oauthClientRef: z.string(),
  scopes: z.array(z.string()),
  capabilities: z.array(z.string()),
  status: connectedAccountStatusSchema,
  /** Derived badge; see `SYNC_HEALTH_STATES`. */
  health: syncHealthSchema,
  lastError: z.string().nullable(),
  lastErrorAt: isoDateTimeSchema.nullable(),
  mailSyncEnabled: z.boolean(),
  calendarSyncEnabled: z.boolean(),
  backfillState: backfillStateSchema,
  connectedAt: isoDateTimeSchema,
  lastSyncedAt: isoDateTimeSchema.nullable(),
});

export const connectedCalendarSchema = z.object({
  id: uuidSchema,
  accountId: uuidSchema,
  providerCalendarId: z.string(),
  summary: z.string(),
  description: z.string().nullable(),
  timezone: z.string().nullable(),
  isPrimary: z.boolean(),
  accessRole: z.string().nullable(),
  /** Whether WE sync it — not whether the provider shows it. */
  syncEnabled: z.boolean(),
  lastSeenAt: isoDateTimeSchema,
});

/** One resource's watermark, with the age an ops dashboard actually reads. */
export const syncWatermarkSchema = z.object({
  resourceKind: z.string(),
  resourceId: z.string(),
  lastSuccessAt: isoDateTimeSchema.nullable(),
  /** `null` when the resource has never succeeded — not 0, which reads as fresh. */
  ageSeconds: z.int().min(0).nullable(),
  consecutiveFailures: z.int().min(0),
  needsFullResync: z.boolean(),
});

export const syncSubscriptionHealthSchema = z.object({
  active: z.int().min(0),
  soonestExpiryAt: isoDateTimeSchema.nullable(),
  /** Negative once a channel has lapsed, which is the alarm condition. */
  secondsToExpiry: z.int().nullable(),
});

export const syncAccountStatusSchema = z.object({
  accountId: uuidSchema,
  provider: syncProviderSchema,
  email: z.string(),
  status: connectedAccountStatusSchema,
  health: syncHealthSchema,
  lastSyncedAt: isoDateTimeSchema.nullable(),
  backfillState: backfillStateSchema,
  watermarks: z.array(syncWatermarkSchema),
  subscriptions: syncSubscriptionHealthSchema,
});

// --- inputs ------------------------------------------------------------------

export const syncAccountsListInputSchema = z.object({
  provider: syncProviderSchema.optional(),
  /** Revoked accounts stay listed by default so the UI can offer a reconnect. */
  includeRevoked: z.boolean().default(true),
});

export const syncAccountsListOutputSchema = z.object({
  items: z.array(connectedAccountSchema),
});

export const syncConnectUrlInputSchema = z.object({
  provider: syncProviderSchema,
  /**
   * Extra scopes on top of the provider default (the "read message bodies"
   * step-up in clients design §2.9). Calendar-only is the default.
   */
  scopes: z.array(z.string().min(1)).max(20).optional(),
  /** Pre-fills the account chooser when reconnecting a known address. */
  loginHint: z.string().max(320).optional(),
});

export const syncConnectUrlOutputSchema = z.object({
  provider: syncProviderSchema,
  /** Send the browser here. Already carries the signed `state`. */
  url: z.string(),
  /** Signed, single-use, short-lived. Echoed back by the provider. */
  state: z.string(),
  expiresAt: isoDateTimeSchema,
});

export const syncAccountDisconnectInputSchema = z.object({
  accountId: uuidSchema,
});

export const syncAccountDisconnectOutputSchema = z.object({
  ok: z.literal(true),
  accountId: uuidSchema,
  status: connectedAccountStatusSchema,
  /**
   * Always true in this release: disconnecting revokes credentials but KEEPS the
   * synced history, because `calendar_events` hangs off the account row.
   * Design §2.9 requires the UI to state which it is.
   */
  historyRetained: z.boolean(),
});

export const syncStatusInputSchema = z.object({
  accountId: uuidSchema.optional(),
});

export const syncStatusOutputSchema = z.object({
  accounts: z.array(syncAccountStatusSchema),
  asOf: isoDateTimeSchema,
});

export const syncCalendarsListInputSchema = z.object({
  accountId: uuidSchema,
});

export const syncCalendarsListOutputSchema = z.object({
  items: z.array(connectedCalendarSchema),
});

export const syncCalendarToggleInputSchema = z.object({
  accountId: uuidSchema,
  providerCalendarId: z.string().min(1).max(500),
  enabled: z.boolean(),
});

// --- inferred types ----------------------------------------------------------

export type ConnectedAccount = z.output<typeof connectedAccountSchema>;
export type ConnectedCalendar = z.output<typeof connectedCalendarSchema>;
export type SyncAccountStatus = z.output<typeof syncAccountStatusSchema>;
export type SyncStatusOutput = z.output<typeof syncStatusOutputSchema>;
